import { forceParsing, language } from "@codemirror/language";
import { EditorView, ViewPlugin, type ViewUpdate } from "@codemirror/view";

// Firefox never delivers IntersectionObserver callbacks for elements living in a different
// window than the observer's constructor realm; @codemirror/view creates its observers with
// the opener window's globals, so in the debugger popout its `intersecting` flag stays false
// and scrolling stops triggering viewport re-measures — force one ourselves on scroll...
export const measureOnScroll = EditorView.domEventHandlers({
    scroll: (_event, view) => {
        view.requestMeasure();
    },
});

// Cap on synchronous parse work per slice, yielding to the event loop in between so very
// large documents don't block the main thread...
const PARSE_SLICE_BUDGET_MS = 50;

// Lezer only parses lazily around the viewport, so fold ranges that end beyond the parse
// frontier (e.g. the root object of a large JSON document) go undetected until the user
// scrolls them into view — drive the parser over the entire document instead...
export const eagerParsing = ViewPlugin.define(view => {
    let pending: ReturnType<typeof setTimeout> | null = null;

    const parseNextSlice = () => {
        // Without a language there is no parser and `forceParsing` reports false forever —
        // bail out rather than reschedule endlessly...
        const done = !view.state.facet(language) || forceParsing(view, view.state.doc.length, PARSE_SLICE_BUDGET_MS);
        pending = done ? null : setTimeout(parseNextSlice);
    };

    // Parsing dispatches view updates, which is illegal from inside an update cycle (both
    // plugin creation and `update` run in one) — always defer to a timeout...
    const schedule = () => {
        if (pending) clearTimeout(pending);
        pending = setTimeout(parseNextSlice);
    };

    schedule();

    return {
        update: (update: ViewUpdate) => {
            if (update.docChanged) schedule();
        },
        destroy: () => {
            if (pending) clearTimeout(pending);
        },
    };
});
